<script lang="ts">
  import { untrack } from 'svelte';
  import Button from '$lib/ui/Button.svelte';
  import Sheet from '$lib/ui/Sheet.svelte';
  import Spinner from '$lib/ui/Spinner.svelte';
  import { createRoomInvite, inviteFriend, me, invitationRoomSnapshot } from './api';
  import { inviteError, roomInviteHref } from './invite-errors';
  import { personName } from './people';
  import { social } from './store';

  let { open = $bindable(false), roomId }: { open?: boolean; roomId: string } = $props();
  const store = social();
  let checking = $state(false);
  let owner = $state(false);
  let error = $state('');
  let notice = $state('');
  let busy = $state('');
  let link = $state('');
  let expiresAt = $state(0);
  let now = $state(Date.now());
  let generation = 0;
  const minutesLeft = $derived(Math.max(1, Math.ceil((expiresAt - now) / 60_000)));

  async function check(): Promise<void> {
    const run = ++generation;
    checking = true; owner = false; error = ''; notice = ''; link = ''; busy = '';
    try {
      const signal = AbortSignal.timeout(15_000);
      const account = await me(signal);
      const found = await invitationRoomSnapshot(roomId, signal);
      if (run !== generation) return;
      if (!found.exists) { error = 'The room is not ready or has ended. Try again once playback starts.'; return; }
      owner = found.room?.ownerUserId === account.me.userId;
      if (!owner) error = 'Only the person who started this room can invite people.';
      if (owner) void store.load();
    } catch (err) { if (run === generation) error = inviteError(err); }
    finally { if (run === generation) checking = false; }
  }
  $effect(() => {
    if (!open) return;
    void roomId;
    untrack(() => void check());
    const tick = setInterval(() => { now = Date.now(); }, 1000);
    return () => { generation += 1; clearInterval(tick); };
  });

  async function generate(): Promise<void> {
    if (busy) return;
    const run = generation;
    busy = 'link'; error = ''; notice = ''; link = '';
    try {
      const result = await createRoomInvite(roomId, AbortSignal.timeout(15_000));
      if (run !== generation) return;
      link = roomInviteHref(location.origin, result.token);
      expiresAt = result.expiresAt;
      now = Date.now();
    } catch (err) { if (run === generation) error = inviteError(err); }
    finally { if (run === generation) busy = ''; }
  }
  async function send(to: string): Promise<void> {
    if (busy) return;
    const run = generation;
    busy = to; error = ''; notice = '';
    try {
      await inviteFriend(roomId, to, AbortSignal.timeout(15_000));
      if (run === generation) notice = 'Invitation sent. They can accept it in Friends.';
    } catch (err) { if (run === generation) error = inviteError(err); }
    finally { if (run === generation) busy = ''; }
  }
  async function copy(): Promise<void> {
    try { await navigator.clipboard.writeText(link); notice = 'Invitation link copied.'; }
    catch { error = 'Copy was blocked. Select the invitation link below and copy it.'; }
  }
  async function shareLink(): Promise<void> {
    try { await navigator.share({ title: 'Watch together', url: link }); }
    catch (err) { if ((err as Error).name !== 'AbortError') error = 'Sharing was blocked. Copy the invitation link instead.'; }
  }
</script>

<Sheet bind:open title="Invite to your room" caption="Invitations expire after 15 minutes.">
  <div class="invite-body">
    {#if checking}
      <p role="status"><Spinner size={15} /> Checking the room…</p>
    {:else if owner}
      <p>Send a friend an invitation, or create a link for one person. They will need to sign in to Jovii before joining.</p>
      <Button icon="link" loading={busy === 'link'} disabled={busy !== ''} onclick={generate}>
        {link ? 'Create a new link' : 'Create invitation link'}
      </Button>
      {#if link && expiresAt > now}
        <label for="room-invitation-link">Invitation link</label>
        <input id="room-invitation-link" readonly value={link} onclick={(event) => event.currentTarget.select()} />
        <div class="actions">
          <Button variant="primary" onclick={copy}>Copy link</Button>
          {#if typeof navigator.share === 'function'}<Button onclick={shareLink}>Share link</Button>{/if}
        </div>
        <p class="fine">Expires in {minutesLeft} minutes. Each link admits one account. Create another link for each guest.</p>
      {:else if link}
        <p role="status">This link has expired. Create a new one.</p>
      {/if}
      <h3>Invite a friend</h3>
      {#if $store.friends.length}
        <ul>
          {#each $store.friends as friend (friend.userId)}
            <li>
              <span><b>{personName(friend)}</b><small>@{friend.handle}</small></span>
              <Button loading={busy === friend.userId} disabled={busy !== ''} onclick={() => send(friend.userId)}>Invite</Button>
            </li>
          {/each}
        </ul>
      {:else}
        <p class="fine">Add someone in Friends to send an in-app invitation.</p>
      {/if}
    {/if}
    {#if error}<p class="bad" role="alert">{error}</p>{/if}
    {#if !checking && !owner}<Button onclick={check}>Check again</Button>{/if}
    {#if notice}<p role="status">{notice}</p>{/if}
  </div>
</Sheet>

<style>
  .invite-body { display: grid; gap: var(--space-3); padding: var(--space-4); }
  p, small { color: var(--text-dim); font-size: var(--text-sm); }
  .fine { color: var(--text-faint); }
  .bad { color: var(--bad, #e0575b); }
  h3 { margin-top: var(--space-3); font-size: var(--text-base); }
  input { width: 100%; min-width: 0; padding: var(--space-3); background: var(--surface-2); color: var(--text); border: 1px solid var(--line); border-radius: var(--radius-sm); }
  input:focus-visible { outline: 2px solid var(--focus-ring); }
  ul { list-style: none; padding: 0; }
  li { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); padding: var(--space-2) 0; border-bottom: 1px solid var(--line); }
  li span { min-width: 0; overflow-wrap: anywhere; }
  small { display: block; }
  .actions { display: flex; flex-wrap: wrap; gap: var(--space-2); }
  :global([data-watch-space]) .invite-body { gap: 8px; padding: 8px 12px; }
  :global([data-watch-space]) .invite-body h3 { margin-top: 8px; font-size: 13px; font-weight: 500; }
  :global([data-watch-space]) .invite-body p { line-height: 1.45; }
  :global([data-watch-space]) .invite-body input { min-height: 44px; padding: 8px; font-size: 16px; }
  :global([data-watch-space]) .invite-body li { padding: 4px 0; border: 0; }
  :global([data-watch-space]) .invite-body :global(.btn) {
    min-height: 44px;
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 500;
    border: 0;
    border-radius: var(--radius-xs);
    background: transparent;
    color: var(--text);
    box-shadow: none;
  }
  :global([data-watch-space]) .invite-body :global(.btn:hover:not(:disabled)) { background: var(--surface-2); }
  :global([data-watch-space]) .invite-body input { border: 0; }
</style>
